import { FormattedMessage, useIntl } from "react-intl";

import Modal from "@components/Modal";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import Heading3 from "@components/typography/Heading3";
import { Opener } from "@hooks/useOpener";

type Props = {
  opener: Opener;
};

const JourneyArchiveModal = ({ opener }: Props) => {
  const intl = useIntl();

  return (
    <Modal opener={opener} title={intl.formatMessage({ id: "pages.expert.journeys.archive.title" })}>
      {() => (
        <>
          <Heading3>
            <FormattedMessage id="pages.expert.journeys.archive.heading1" />
          </Heading3>
          <ul>
            <li>
              <BodyCopy>
                <FormattedMessage id="pages.expert.journeys.archive.heading1Step1" />
              </BodyCopy>
            </li>
            <li>
              <BodyCopy>
                <FormattedMessage id="pages.expert.journeys.archive.heading1Step2" />
              </BodyCopy>
            </li>
            <li>
              <BodyCopy>
                <FormattedMessage id="pages.expert.journeys.archive.heading1Step3" />
              </BodyCopy>
            </li>
            <ul>
              <li>
                <BodyCopy>
                  <FormattedMessage
                    id="pages.expert.journeys.archive.heading1Step3Sub"
                    values={{
                      immediately: (
                        <BodyCopy bold>
                          <FormattedMessage id="pages.expert.journeys.archive.immediately" />
                        </BodyCopy>
                      )
                    }}
                  />
                </BodyCopy>
              </li>
            </ul>
          </ul>
          <div>
            <Heading3>
              <FormattedMessage id="pages.expert.journeys.archive.heading2" />
            </Heading3>
            <div>
              <BodyCopy bold>
                <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading1" />
              </BodyCopy>
            </div>
            <ul>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading1Step1" />
                </BodyCopy>
              </li>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading1Step2" />
                </BodyCopy>
              </li>
              <li>
                <BodyCopy>
                  <FormattedMessage
                    id="pages.expert.journeys.archive.heading2SubHeading1Step3"
                    values={{
                      manuallyArchived: (
                        <BodyCopy bold>
                          <FormattedMessage id="pages.expert.journeys.archive.manuallyArchived" />
                        </BodyCopy>
                      )
                    }}
                  />
                </BodyCopy>
              </li>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading1Step4" />
                </BodyCopy>
              </li>
            </ul>
            <div>
              <BodyCopy bold>
                <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2" />
              </BodyCopy>
              <ul>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step1" />
                  </BodyCopy>
                </li>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step2" />
                  </BodyCopy>
                </li>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step3" />
                  </BodyCopy>
                </li>
                <ul>
                  <li>
                    <BodyCopy>
                      <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step3Sub1" />
                    </BodyCopy>
                  </li>
                  <li>
                    <BodyCopy>
                      <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step3Sub2" />
                    </BodyCopy>
                  </li>
                  <li>
                    <BodyCopy>
                      <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step3Sub3" />
                    </BodyCopy>
                  </li>
                  <li>
                    <BodyCopy>
                      <FormattedMessage id="pages.expert.journeys.archive.heading2SubHeading2Step3Sub4" />
                    </BodyCopy>
                  </li>
                </ul>
              </ul>
            </div>
            <Heading3>
              <FormattedMessage id="pages.expert.journeys.archive.heading3" />
            </Heading3>
            <div>
              <BodyCopy bold>
                <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading1" />
              </BodyCopy>
            </div>
            <ul>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading1Step1" />
                </BodyCopy>
                <ul>
                  <li>
                    <BodyCopy>
                      <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading1Step1Sub1" />
                    </BodyCopy>
                  </li>
                </ul>
              </li>
            </ul>
            <div>
              <BodyCopy bold>
                <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2" />
              </BodyCopy>
            </div>
            <ul>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2Step1" />
                </BodyCopy>
              </li>
              <li>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2Step2" />
                </BodyCopy>
              </li>
              <ul>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2Step2Sub1" />
                  </BodyCopy>
                </li>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2Step2Sub2" />
                  </BodyCopy>
                </li>
                <li>
                  <BodyCopy>
                    <FormattedMessage id="pages.expert.journeys.archive.heading3SubHeading2Step2Sub3" />
                  </BodyCopy>
                </li>
              </ul>
            </ul>
            <div>
              <BodyCopy>
                <FormattedMessage
                  id="pages.expert.journeys.archive.businessDays"
                  values={{
                    companyHolidays: (
                      <ExternalLink href="https://www.notion.so/withgrayce/Holidays-f25e09e1cc5c43aaade58800eb57357e">
                        <FormattedMessage id="pages.expert.journeys.archive.companyHolidays" />
                      </ExternalLink>
                    )
                  }}
                />
              </BodyCopy>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default JourneyArchiveModal;
